'use client';

import { getCurrentPetOwnerAccount } from '@/lib/utils/api-owner';
import { useCurrentUserProfile } from '@/lib/utils/api-shared';
import type { Owner, OwnerAccount } from '@lib/types/owner';
import { useQuery } from '@tanstack/react-query';
import {
  AppointmentsIcon,
  NewAppointmentIcon,
  PawIcon,
  SmileIcon,
} from '@ui/Icons';
import { NavigationGroup } from '@ui/layout/Layout';
import { CreditCardIcon } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';

/**
 * Pet Owner Onboarding System
 *
 * This module manages the onboarding flow for pet owners, ensuring that users
 * who haven't accepted Petboost's terms and conditions or need to add a card on file
 * are redirected to complete their profile and have restricted navigation until complete.
 *
 * Key Features:
 * - Automatic redirect to /profile for users without accepted terms or missing card
 * - Restricted navigation based on requirements:
 *   - Card required: Only "My Profile" available
 *   - Terms not accepted: "My Profile" and "Book Now" available
 * - Seamless integration with existing authentication flow
 * - Proper loading states and error handling
 */

// Navigation configurations
export const authenticatedNavigation: NavigationGroup[] = [
  [
    {
      name: 'Appointments',
      href: '/appointments',
      icon: AppointmentsIcon,
      current: false,
    },
    {
      name: 'Book Now',
      href: '/booking-form',
      icon: NewAppointmentIcon,
      current: false,
    },
    {
      name: 'My Pets',
      href: '/pets',
      icon: PawIcon,
      current: false,
    },
  ],
  [
    {
      name: 'Payments & Billing',
      href: '/payments',
      icon: CreditCardIcon,
      current: false,
    },
  ],
  [
    {
      name: 'My Profile',
      href: '/profile',
      icon: SmileIcon,
      current: false,
    },
  ],
];

export const anonymousNavigation: NavigationGroup[] = [
  [
    {
      name: 'Book Now',
      href: '/booking-form',
      icon: NewAppointmentIcon,
      current: true,
    },
  ],
];

// Restricted navigation for users who haven't accepted terms
export const termsNotAcceptedNavigation: NavigationGroup[] = [
  [
    {
      name: 'My Profile',
      href: '/profile',
      icon: SmileIcon,
      current: false,
    },
    {
      name: 'Book Now',
      href: '/booking-form',
      icon: NewAppointmentIcon,
      current: false,
    },
  ],
];

// Highly restricted navigation for users who need to add card on file
export const cardRequiredNavigation: NavigationGroup[] = [
  [
    {
      name: 'My Profile',
      href: '/profile',
      icon: SmileIcon,
      current: false,
    },
  ],
];

/**
 * Routes that are allowed when terms haven't been accepted
 * These routes remain accessible during the onboarding process
 */
const ALLOWED_ROUTES_WITHOUT_TERMS = [
  '/profile', // Required for completing onboarding
  '/booking-form', // Booking should remain accessible
  '/complete-pet-details', // Allow completing pet details during booking flow
  '/auth', // Authentication routes
];

/**
 * Routes that are allowed when card is required
 * Only profile is accessible when card must be added
 */
const ALLOWED_ROUTES_CARD_REQUIRED = [
  '/profile', // Required for completing card setup
  '/complete-pet-details', // Allow completing pet details during booking flow
  '/auth', // Authentication routes
];

// Type for the hook return value
interface PetOwnerOnboardingState {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;

  // Onboarding state
  needsOnboarding: boolean;
  termsAndConditionsChecked: boolean;

  // Data
  currentUserProfile: Awaited<ReturnType<typeof useCurrentUserProfile>>['data'];
  owner: Owner | undefined;
  account: OwnerAccount | undefined;
  petOwnerData:
    | Awaited<ReturnType<typeof getCurrentPetOwnerAccount>>
    | undefined;

  // Navigation
  navigation: NavigationGroup[];

  // Route checking
  isCurrentRouteAllowed: boolean;
}

/**
 * Hook to manage pet owner onboarding state and navigation
 *
 * This hook handles the complete onboarding flow:
 * 1. Checks if user is authenticated
 * 2. Fetches pet owner account data to check terms acceptance
 * 3. Determines appropriate navigation based on onboarding status
 * 4. Automatically redirects users to /profile if terms not accepted
 * 5. Provides loading states during data fetching
 *
 * @returns {PetOwnerOnboardingState} Complete onboarding state and navigation
 *
 * @example
 * ```tsx
 * function Layout({ children }) {
 *   const { isAuthenticated, navigation, isLoading } = usePetOwnerOnboarding();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <AppLayout navigation={navigation} isAuthenticated={isAuthenticated}>
 *       {children}
 *     </AppLayout>
 *   );
 * }
 * ```
 */
export const usePetOwnerOnboarding = (): PetOwnerOnboardingState => {
  const router = useRouter();
  const pathname = usePathname();

  // Get user profile data
  const { data: currentUserProfile, isLoading: isUserProfileLoading } =
    useCurrentUserProfile();

  // Get pet owner account data (only if user is authenticated)
  const { data: petOwnerData, isLoading: isPetOwnerLoading } = useQuery({
    queryKey: ['currentPetOwnerAccount'],
    queryFn: getCurrentPetOwnerAccount,
    enabled: !!currentUserProfile,
    retry: false,
  });

  // Determine authentication status
  const isAuthenticated = !!currentUserProfile;

  // Find the owner that matches the current user's account ID
  const owner = petOwnerData?.petOwners.find(
    (owner) => owner.accountId === currentUserProfile?.accountInfo.id
  );
  const account = petOwnerData?.petOwnerAccount;

  // Check if terms and conditions have been accepted
  const termsAndConditionsChecked = account?.termsAndConditionsChecked === true;

  // Check if card on file is required
  const cardRequired = account?.requireCardOnFile === true;

  // Determine if user needs to complete onboarding (terms OR card requirements)
  const needsOnboarding =
    isAuthenticated && (!termsAndConditionsChecked || cardRequired);

  // Determine loading state
  const isLoading =
    isUserProfileLoading || (isAuthenticated && isPetOwnerLoading);

  // Check if current route is allowed based on onboarding requirements
  const isCurrentRouteAllowed = pathname
    ? (() => {
        if (!isAuthenticated || !needsOnboarding) {
          return true; // No restrictions if not authenticated or onboarding complete
        }

        // If card is required (even if terms are accepted), use stricter restrictions
        if (cardRequired) {
          return ALLOWED_ROUTES_CARD_REQUIRED.some((route) =>
            pathname.startsWith(route)
          );
        }

        // If only terms are not accepted, use less restrictive rules
        return ALLOWED_ROUTES_WITHOUT_TERMS.some((route) =>
          pathname.startsWith(route)
        );
      })()
    : false;

  // Determine appropriate navigation
  const getNavigation = (): NavigationGroup[] => {
    if (!isAuthenticated) {
      return anonymousNavigation;
    }

    if (needsOnboarding) {
      // If card is required, show highly restricted navigation (only My Profile)
      if (cardRequired) {
        return cardRequiredNavigation;
      }
      // If only terms are not accepted, show less restricted navigation
      return termsNotAcceptedNavigation;
    }

    return authenticatedNavigation;
  };

  // Handle redirect logic
  useEffect(() => {
    // Don't redirect if still loading
    if (isLoading) return;

    // Don't redirect if user is not authenticated
    if (!isAuthenticated) return;

    // Don't redirect if profile completion is not needed
    if (!needsOnboarding) return;

    // Don't redirect if already on an allowed route
    if (isCurrentRouteAllowed) return;

    // Redirect to profile for onboarding
    router.push('/profile');
  }, [
    isLoading,
    isAuthenticated,
    needsOnboarding,
    isCurrentRouteAllowed,
    router,
    pathname,
  ]);

  return {
    // Authentication state
    isAuthenticated,
    isLoading,

    // Onboarding state
    needsOnboarding,
    termsAndConditionsChecked,

    // Data
    currentUserProfile,
    owner,
    account,
    petOwnerData,

    // Navigation
    navigation: getNavigation(),

    // Route checking
    isCurrentRouteAllowed,
  };
};

/**
 * Utility function to check if a user has completed onboarding
 *
 * @param account - The pet owner account to check
 * @returns {boolean} True if terms and conditions have been accepted AND card requirement is fulfilled
 */
export const hasCompletedOnboarding = (
  account: OwnerAccount | null | undefined
): boolean => {
  const termsAccepted = account?.termsAndConditionsChecked === true;
  const cardRequired = account?.requireCardOnFile === true;

  // If card is required, onboarding is only complete when both terms are accepted AND card requirement is fulfilled
  // If card is not required, onboarding is complete when terms are accepted
  return termsAccepted && !cardRequired;
};

/**
 * Utility function to check if a route requires profile completion
 *
 * @param pathname - The route pathname to check
 * @param cardRequired - Whether card on file is required
 * @returns {boolean} True if the route requires profile completion
 */
export const requiresProfileCompletion = (
  pathname: string,
  cardRequired: boolean = false
): boolean => {
  const allowedRoutes = cardRequired
    ? ALLOWED_ROUTES_CARD_REQUIRED
    : ALLOWED_ROUTES_WITHOUT_TERMS;

  return !allowedRoutes.some((route) => pathname.startsWith(route));
};
